import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  flex: 1;
  max-width: 280px;
`;

const ToolbarLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.65);
`;

const SearchInput = styled.input`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const FilterSelect = styled.select`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.9rem;
  min-width: 140px;

  option {
    color: #1a202c;
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  color: white;
  font-size: 1.8rem;
  margin: 0;
`;

const Button = styled.button`
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.$primary && `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }
  `}

  ${props => props.$danger && `
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
    &:hover {
      background: rgba(239, 68, 68, 0.3);
    }
  `}

  ${props => props.$success && `
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.3);
    &:hover {
      background: rgba(34, 197, 94, 0.3);
    }
  `}

  ${props => props.$secondary && `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableContainer = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: rgba(255, 255, 255, 0.05);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatusBadge = styled.span`
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  ${props => props.$active && `
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.3);
  `}

  ${props => !props.$active && `
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  `}
`;

const ActionCell = styled(TableCell)`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const PaginationInfo = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: rgba(30, 41, 59, 0.98);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  margin: 0 0 1.5rem 0;
`;

const ModalField = styled.div`
  margin-bottom: 1.25rem;
`;

const ModalLabel = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
`;

function CollegeManagement() {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalCount: 0, totalPages: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingCollege, setEditingCollege] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    state: '',
    country: 'India',
    university_id: '',
    is_active: true
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, college: null });
  const [universities, setUniversities] = useState([]);

  const token = localStorage.getItem('adminToken');
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const fetchColleges = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(search && { search }),
        ...(isActiveFilter && { is_active: isActiveFilter })
      });

      const response = await fetch(`${API_BASE}/api/v1/admin/colleges?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setColleges(data.data);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch colleges:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, isActiveFilter, token, API_BASE]);

  const fetchUniversities = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/admin/universities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        setUniversities(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch universities:', error);
    }
  }, [token, API_BASE]);

  useEffect(() => {
    fetchColleges();
    fetchUniversities();
  }, [fetchColleges, fetchUniversities]);

  const handleCreate = () => {
    setEditingCollege(null);
    setFormData({
      name: '',
      city: '',
      state: '',
      country: 'India',
      university_id: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (college) => {
    setEditingCollege(college);
    setFormData({
      name: college.name,
      city: college.city || '',
      state: college.state || '',
      country: college.country || 'India',
      university_id: college.university_id || '',
      is_active: college.is_active
    });
    setShowModal(true);
  };

  const handleDelete = async (college) => {
    setDeleteModal({ open: true, college });
  };

  const confirmDelete = async () => {
    if (!deleteModal.college) return;

    try {
      const response = await fetch(`${API_BASE}/api/v1/admin/colleges/${deleteModal.college.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchColleges();
        setDeleteModal({ open: false, college: null });
      } else {
        alert(data.message || 'Failed to delete college');
      }
    } catch (error) {
      console.error('Failed to delete college:', error);
      alert('Failed to delete college');
    }
  };

  const handleToggleActive = async (college) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/admin/colleges/${college.id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !college.is_active })
      });

      const data = await response.json();
      if (data.success) {
        fetchColleges();
      } else {
        alert(data.message || 'Failed to update college status');
      }
    } catch (error) {
      console.error('Failed to toggle college status:', error);
      alert('Failed to update college status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCollege 
        ? `${API_BASE}/api/v1/admin/colleges/${editingCollege.id}`
        : `${API_BASE}/api/v1/admin/colleges`;
      
      const method = editingCollege ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchColleges();
      } else {
        alert(data.message || 'Failed to save college');
      }
    } catch (error) {
      console.error('Failed to save college:', error);
      alert('Failed to save college');
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleFilterChange = (e) => {
    setIsActiveFilter(e.target.value);
    setPage(1);
  };

  return (
    <Container>
      <TitleRow>
        <Title>College Management</Title>
        <Button $primary onClick={handleCreate}>
          + Add College
        </Button>
      </TitleRow>

      <Toolbar>
        <ToolbarLeft>
          <Field>
            <ToolbarLabel>Search</ToolbarLabel>
            <SearchInput
              placeholder="Search by name, city, state..."
              value={search}
              onChange={handleSearchChange}
            />
          </Field>
          <Field>
            <ToolbarLabel>Status</ToolbarLabel>
            <FilterSelect value={isActiveFilter} onChange={handleFilterChange}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </FilterSelect>
          </Field>
        </ToolbarLeft>
      </Toolbar>

      <TableContainer>
        {loading ? (
          <LoadingSpinner>Loading colleges...</LoadingSpinner>
        ) : colleges.length === 0 ? (
          <EmptyState>No colleges found</EmptyState>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>University</TableHeaderCell>
                  <TableHeaderCell>City</TableHeaderCell>
                  <TableHeaderCell>State</TableHeaderCell>
                  <TableHeaderCell>Country</TableHeaderCell>
                  <TableHeaderCell>Students</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {colleges.map((college) => (
                  <TableRow key={college.id}>
                    <TableCell>{college.name}</TableCell>
                    <TableCell>{college.university_id ? 'Linked' : '-'}</TableCell>
                    <TableCell>{college.city || '-'}</TableCell>
                    <TableCell>{college.state || '-'}</TableCell>
                    <TableCell>{college.country || '-'}</TableCell>
                    <TableCell>{college.student_count || 0}</TableCell>
                    <TableCell>
                      <StatusBadge $active={college.is_active}>
                        {college.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </TableCell>
                    <ActionCell>
                      <Button $secondary onClick={() => handleEdit(college)}>
                        Edit
                      </Button>
                      <Button 
                        $success={!college.is_active}
                        $danger={college.is_active}
                        onClick={() => handleToggleActive(college)}
                      >
                        {college.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      {!college.is_system && (
                        <Button $danger onClick={() => handleDelete(college)}>
                          Delete
                        </Button>
                      )}
                    </ActionCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            <Pagination>
              <PaginationInfo>
                Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, pagination.totalCount)} of {pagination.totalCount} colleges
              </PaginationInfo>
              <PaginationControls>
                <Button 
                  $secondary 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button 
                  $secondary 
                  onClick={() => setPage(p => p + 1)}
                  disabled={!pagination.hasNext}
                >
                  Next
                </Button>
              </PaginationControls>
            </Pagination>
          </>
        )}
      </TableContainer>

      {showModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <ModalContent>
            <ModalTitle>{editingCollege ? 'Edit College' : 'Add New College'}</ModalTitle>
            <form onSubmit={handleSubmit}>
              <ModalField>
                <ModalLabel>College Name *</ModalLabel>
                <ModalInput
                  required
                  minLength={3}
                  maxLength={255}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Indian Institute of Technology Bombay"
                />
              </ModalField>
              <ModalField>
                <ModalLabel>University</ModalLabel>
                <FilterSelect
                  value={formData.university_id}
                  onChange={(e) => setFormData({ ...formData, university_id: e.target.value })}
                >
                  <option value="">Select University (Optional)</option>
                  {universities.map((university) => (
                    <option key={university.id} value={university.id}>
                      {university.name}
                    </option>
                  ))}
                </FilterSelect>
              </ModalField>
              <ModalField>
                <ModalLabel>City</ModalLabel>
                <ModalInput
                  maxLength={150}
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="e.g., Mumbai"
                />
              </ModalField>
              <ModalField>
                <ModalLabel>State</ModalLabel>
                <ModalInput
                  maxLength={150}
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="e.g., Maharashtra"
                />
              </ModalField>
              <ModalField>
                <ModalLabel>Country</ModalLabel>
                <ModalInput
                  maxLength={100}
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g., India"
                />
              </ModalField>
              <ModalField>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'rgba(255,255,255,0.8)' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </ModalField>
              <ModalActions>
                <Button $secondary type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button $primary type="submit">
                  {editingCollege ? 'Update' : 'Create'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}

      {deleteModal.open && (
        <Modal onClick={(e) => e.target === e.currentTarget && setDeleteModal({ open: false, college: null })}>
          <ModalContent>
            <ModalTitle>Confirm Delete</ModalTitle>
            <div style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '1.5rem' }}>
              Are you sure you want to delete <strong>{deleteModal.college?.name}</strong>? This action cannot be undone.
            </div>
            <ModalActions>
              <Button $secondary type="button" onClick={() => setDeleteModal({ open: false, college: null })}>
                Cancel
              </Button>
              <Button $danger type="button" onClick={confirmDelete}>
                Delete
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default CollegeManagement;
